import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { environment } from '../environments/environment';
import { provideApiConfiguration } from './api/generated/api-configuration';
import { routes } from './app.routes';
import Preset from './preset';
import { ErrorNotificationService } from './shared/services/error-notification.service';
import { HttpErrorService } from './shared/services/http-error.service';
import { MockMsalBroadcastService, MockMsalGuard, MockMsalService } from './shared/services/mock-msal.service';

export function loggerCallback(logLevel: LogLevel, message: string) {
  if (logLevel <= LogLevel.Error) {
    console.error(message);
  }
  if (logLevel === LogLevel.Warning) {
    console.warn(message);
  }
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    environment.apiConfig.uri,
    environment.apiConfig.scopes
  );
  protectedResourceMap.set(
    environment.fleetApiUrl,
    environment.apiConfig.scopes
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

/** Providers for online mode (Entra ID / MSAL) */
function getMsalProviders() {
  return [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ];
}

/** Providers for offline mode (no Entra ID required) */
function getOfflineProviders() {
  return [
    { provide: MsalService, useClass: MockMsalService },
    { provide: MsalGuard, useClass: MockMsalGuard },
    { provide: MsalBroadcastService, useClass: MockMsalBroadcastService },
    // Provide MSAL tokens so any remaining injection points don't fail
    { provide: MSAL_INSTANCE, useFactory: () => ({}) },
    { provide: MSAL_GUARD_CONFIG, useValue: {} },
    { provide: MSAL_INTERCEPTOR_CONFIG, useValue: { interactionType: 0, protectedResourceMap: new Map() } },
  ];
}

const isOffline = !!(environment as any).offline;

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Preset,
        options: {
          prefix: 'p',
          darkModeSelector: '.dark',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng'
          }
        }
      }
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    importProvidersFrom(BrowserModule),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    // Configure the generated API client root URL from environment
    provideApiConfiguration(environment.apiRootUrl),
    // Auth providers: MSAL for online, mocks for offline
    ...(isOffline ? getOfflineProviders() : getMsalProviders()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorService,
      multi: true
    },
    MessageService,
    ConfirmationService,
    {
      provide: ErrorHandler,
      useClass: ErrorNotificationService
    },
    {
      provide: LOCALE_ID,
      useValue: 'en-US'
    }
  ],
};
